import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  createVideoRecorder,
  MAX_RECORDING_MS,
  type CameraFacing,
  type VideoRecorder,
  type VideoRecordingResult,
} from '@/lib/videoRecorder';
import { devLog } from '@/lib/utils';
import RevealText from './RevealText';

interface VideoReplyModalProps {
  isOpened: boolean;
  onClose: () => void;
  /** Called with the recorded clip (+ its parallel audio track, when the
   *  browser could capture one) the moment the user stops recording — there
   *  is no review step. The sheet stays open afterwards: the camera preview
   *  never drops and the interviewer's next question is overlaid on it, so
   *  the contributor answers question after question without leaving video
   *  mode. */
  onSubmit: (result: VideoRecordingResult) => void;
  /** The interviewer's current question, overlaid on the live preview. */
  questionText?: string;
  /** True while the last answer is uploading / the next question is being
   *  prepared — the overlay shows a thinking indicator instead of the (stale)
   *  previous question. */
  isThinking?: boolean;
  /** The rotating phrase shown while thinking (see useThinkingPhrase). */
  thinkingPhrase?: string;
  /** True while the interviewer is speaking the question aloud. Recording is
   *  held until it finishes so the mic doesn't pick up the question audio. */
  isSpeaking?: boolean;
  /** 0..1 progress of the spoken question's audio playback — drives the same
   *  word-by-word reveal the main screen uses for its question text. */
  playbackProgress?: number;
  /** Opens the attachment picker over the sheet, so photos, videos and docs
   *  can be added without leaving video mode. No button when absent. */
  onOpenAttachments?: () => void;
  /** Files attached so far, shown as a badge on the attach button. */
  attachmentCount?: number;
  /** Passes on the current question without recording an answer — the same
   *  reply the main screen's Skip sends ("pass", or "skip" in a game). No
   *  button when absent. */
  onSkip?: () => void;
  /** Fun & Games: tap-to-answer options for the current question, so a game
   *  can be played without leaving video mode. */
  gameChoices?: string[];
  /** Fun & Games: the game's secondary buttons (Roll again, I'm done). */
  gameActions?: { label: string; reply: string }[];
  /** Sends a choice's text, or an action's reply, as the answer. No game
   *  buttons when absent. */
  onGameReply?: (text: string) => void;
  /** Fun & Games: a photo is the best answer here, so Attach leads. */
  wantsPhoto?: boolean;
}

type Phase = 'starting' | 'preview' | 'recording' | 'error';

/** Height the question overlay may take over the video: a third of the screen. */
const overlayRoom = (): number => Math.round(window.innerHeight * 0.34);

const formatClock = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/** Full-screen camera sheet for recording a live video answer. The contributor
 *  frames themselves, records (up to 3 minutes), and the take is sent as their
 *  answer the moment they stop — no review step. The parent owns everything
 *  after submit: upload, transcription, and the interviewer's reply. */
function VideoReplyModal({
  isOpened,
  onClose,
  onSubmit,
  questionText,
  isThinking,
  thinkingPhrase = 'Thinking',
  isSpeaking,
  playbackProgress = 1,
  onOpenAttachments,
  attachmentCount = 0,
  onSkip,
  gameChoices = [],
  gameActions = [],
  onGameReply,
  wantsPhoto,
}: VideoReplyModalProps) {
  const recorderRef = useRef<VideoRecorder | null>(null);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const autoStopRef = useRef<number | null>(null);
  const recordingStartRef = useRef(0);

  const [phase, setPhase] = useState<Phase>('starting');
  const [facing, setFacing] = useState<CameraFacing>('user');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  // ── Question overlay typography — the same serif treatment as the main
  //    screen's question (Newsreader, weight 300, word-by-word reveal synced
  //    to the spoken audio), re-inked white with a scrim to sit over video. ──
  // The overlay may take a third of the screen; the question is fitted to that
  // (see RevealText) instead of scrolling inside it.
  const [questionRoom, setQuestionRoom] = useState(() => overlayRoom());
  useEffect(() => {
    const onResize = () => setQuestionRoom(overlayRoom());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Length-aware soft shrink like the main screen, one notch smaller overall
  // so the question shares the frame with the contributor's face.
  const questionFontSize = useMemo(() => {
    const len = questionText?.length ?? 0;
    if (len > 220) return 'clamp(15px, 2.2vw, 24px)';
    if (len > 140) return 'clamp(17px, 2.6vw, 28px)';
    return 'clamp(20px, 3vw, 32px)';
  }, [questionText]);

  const clearTimers = () => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current != null) {
      window.clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  };

  const teardown = useCallback(() => {
    clearTimers();
    recorderRef.current?.destroy();
    recorderRef.current = null;
    if (liveVideoRef.current) liveVideoRef.current.srcObject = null;
  }, []);

  const openCamera = useCallback(
    async (facingToUse: CameraFacing) => {
      setPhase('starting');
      try {
        if (!recorderRef.current) recorderRef.current = createVideoRecorder();
        const stream = await recorderRef.current.openCamera(facingToUse);
        if (liveVideoRef.current) {
          liveVideoRef.current.srcObject = stream;
          // play() can reject harmlessly if the modal is closing mid-open.
          liveVideoRef.current.play().catch(() => undefined);
        }
        setPhase('preview');
      } catch (error: any) {
        devLog.warn('[VideoReplyModal] camera open failed:', error?.message || error);
        setErrorMessage(
          error?.name === 'NotAllowedError'
            ? 'Camera access was blocked. Allow camera and microphone access in your browser settings, then try again.'
            : 'The camera could not be started on this device.',
        );
        setPhase('error');
      }
    },
    [],
  );

  // Open the camera when the sheet opens; release everything when it closes.
  useEffect(() => {
    if (!isOpened) {
      teardown();
      setElapsedMs(0);
      setErrorMessage('');
      setPhase('starting');
      return;
    }
    openCamera(facing);
    return () => {
      teardown();
    };
    // `facing` changes are handled by handleFlipCamera, not by re-running this
    // open/close effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpened]);

  const handleFlipCamera = async () => {
    if (phase !== 'preview') return;
    const next: CameraFacing = facing === 'user' ? 'environment' : 'user';
    setFacing(next);
    await openCamera(next);
  };

  /** Stop capturing and send the take as the answer immediately — no review
   *  step. stopRecording() leaves the camera stream live, so the sheet drops
   *  straight back to the preview (with the interviewer's "thinking" overlay)
   *  without re-opening the camera. */
  const stopAndSend = useCallback(async () => {
    if (!recorderRef.current?.isRecording()) return;
    clearTimers();
    try {
      const result = await recorderRef.current.stopRecording();
      setElapsedMs(0);
      if (result.videoBlob.size < 1024) {
        teardown();
        setErrorMessage('That recording came out empty — give it another try.');
        setPhase('error');
        return;
      }
      setPhase('preview');
      onSubmit(result);
    } catch (error: any) {
      devLog.warn('[VideoReplyModal] stop failed:', error?.message || error);
      teardown();
      setErrorMessage('The recording could not be saved. Please try again.');
      setPhase('error');
    }
  }, [onSubmit, teardown]);

  const handleRecord = () => {
    if (phase !== 'preview' || !recorderRef.current) return;
    // Held while the next question is being prepared/spoken (the button is
    // also visually disabled) — belt and braces for programmatic clicks.
    if (isThinking || isSpeaking) return;
    try {
      // The size-limit callback mirrors the duration timer below: some
      // browsers ignore the requested bitrate, so the recorder also stops
      // when the take's actual bytes reach the upload caps.
      recorderRef.current.beginRecording(() => {
        void stopAndSend();
      });
      recordingStartRef.current = Date.now();
      setElapsedMs(0);
      setPhase('recording');
      timerRef.current = window.setInterval(() => {
        setElapsedMs(Date.now() - recordingStartRef.current);
      }, 250);
      autoStopRef.current = window.setTimeout(() => {
        void stopAndSend();
      }, MAX_RECORDING_MS);
    } catch (error: any) {
      devLog.warn('[VideoReplyModal] record failed:', error?.message || error);
      setErrorMessage('Recording could not be started on this device.');
      setPhase('error');
    }
  };

  const handleClose = () => {
    if (recorderRef.current?.isRecording()) {
      // Discard the in-flight take.
      clearTimers();
      recorderRef.current.destroy();
      recorderRef.current = null;
    }
    onClose();
  };

  if (!isOpened) return null;

  const isRecording = phase === 'recording';
  const remainingMs = MAX_RECORDING_MS - elapsedMs;
  const nearLimit = isRecording && remainingMs < 30_000;
  // Between answers the sheet stays open: while the last answer uploads and
  // the interviewer prepares/speaks the next question, recording is held.
  const waitingForQuestion = !!isThinking || !!isSpeaking;
  const showQuestionOverlay =
    (phase === 'starting' || phase === 'preview' || phase === 'recording') &&
    (waitingForQuestion || !!questionText);
  const showGameButtons =
    !!onGameReply && !isThinking && (gameChoices.length > 0 || gameActions.length > 0);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Record a video answer"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#101410',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Stage */}
      <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        {/* Live preview (kept mounted through preview + recording). */}
        <video
          ref={liveVideoRef}
          autoPlay
          muted
          playsInline
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Mirror the selfie camera so the preview behaves like a mirror.
            transform: facing === 'user' ? 'scaleX(-1)' : undefined,
            display: phase === 'error' ? 'none' : 'block',
          }}
        />

        {phase === 'starting' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              color: 'rgba(255,255,255,0.85)',
              fontSize: 14,
              letterSpacing: '0.08em',
            }}
          >
            Starting camera…
          </div>
        )}

        {phase === 'error' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              padding: 32,
            }}
          >
            <div
              style={{
                maxWidth: 420,
                textAlign: 'center',
                color: 'rgba(255,255,255,0.92)',
                fontSize: 15,
                lineHeight: 1.5,
              }}
            >
              {errorMessage}
            </div>
          </div>
        )}

        {/* Question overlay — the interviewer's next question rides on top of
            the live selfie preview so the contributor never has to leave
            video mode between answers. While the previous answer uploads the
            (stale) question is swapped for a thinking indicator. Rendered
            before the top bar so the close/flip buttons stay tappable. */}
        {showQuestionOverlay && (
          <div
            aria-live="polite"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              padding: 'calc(env(safe-area-inset-top, 0px) + 64px) 20px 40px',
              background:
                'linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.30) 65%, transparent)',
              pointerEvents: 'none',
            }}
          >
            {isThinking ? (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'rgba(255,255,255,0.85)',
                  fontFamily: 'var(--lp-mono)',
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                }}
              >
                <span key={thinkingPhrase} className="lp-thinking-phrase">
                  {thinkingPhrase}…
                </span>
              </div>
            ) : (
              <RevealText
                key={questionText}
                text={questionText}
                progress={playbackProgress}
                fontSize={questionFontSize}
                minFontSize={14}
                room={questionRoom}
                className="lp-serif"
                style={{
                  margin: 0,
                  maxWidth: 680,
                  pointerEvents: 'auto',
                  textAlign: 'center',
                  color: '#fff',
                  fontWeight: 300,
                  lineHeight: 1.24,
                  letterSpacing: '-0.018em',
                  textShadow: '0 1px 2px rgba(0,0,0,0.45), 0 2px 16px rgba(0,0,0,0.55)',
                  textWrap: 'balance',
                }}
              />
            )}
          </div>
        )}

        {/* Top bar: close + timer */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'calc(env(safe-area-inset-top, 0px) + 14px) 16px 12px',
            background: 'linear-gradient(rgba(0,0,0,0.45), transparent)',
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close video recorder"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.35)',
              background: 'rgba(0,0,0,0.35)',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
            }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>

          {(isRecording || phase === 'preview') && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 14px',
                borderRadius: 999,
                background: 'rgba(0,0,0,0.45)',
                color: nearLimit ? '#ffb4a2' : '#fff',
                fontVariantNumeric: 'tabular-nums',
                fontSize: 14,
              }}
            >
              {isRecording && (
                <span
                  aria-hidden
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#ff5252',
                    animation: 'lp-record-pulse 1.2s ease-in-out infinite',
                  }}
                />
              )}
              {isRecording
                ? `${formatClock(elapsedMs)} · ${formatClock(remainingMs)} left`
                : `Up to ${formatClock(MAX_RECORDING_MS)}`}
            </div>
          )}

          {phase === 'preview' ? (
            <button
              type="button"
              onClick={handleFlipCamera}
              aria-label="Switch camera"
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '1px solid rgba(255,255,255,0.35)',
                background: 'rgba(0,0,0,0.35)',
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                cursor: 'pointer',
              }}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
                <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
                <path d="m18 22 3-3-3-3" />
                <path d="m6 2 -3 3 3 3" />
              </svg>
            </button>
          ) : (
            <span style={{ width: 40 }} aria-hidden />
          )}
        </div>

        {/* Bottom controls */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 14,
            padding: '18px 16px calc(env(safe-area-inset-bottom, 0px) + 22px)',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.55))',
          }}
        >
          {phase === 'preview' && (
            <>
              {/* Fun & Games buttons ride with the question: hidden while the
                  next one is prepared (the overlay hides the stale question
                  too), held while it is spoken. */}
              {showGameButtons && (
                <div
                  style={{
                    width: '100%',
                    maxWidth: 520,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  {gameChoices.length > 0 && (
                    <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
                      {gameChoices.map((choice) => (
                        <button
                          key={choice}
                          type="button"
                          onClick={() => onGameReply?.(choice)}
                          disabled={waitingForQuestion}
                          style={{
                            flex: '1 1 130px',
                            minHeight: 48,
                            padding: '10px 16px',
                            borderRadius: '2px 14px 2px 14px',
                            border: '1px solid rgba(255,255,255,0.55)',
                            background: 'rgba(0,0,0,0.38)',
                            backdropFilter: 'blur(6px)',
                            WebkitBackdropFilter: 'blur(6px)',
                            color: '#fff',
                            fontFamily: 'var(--lp-serif)',
                            fontSize: 17,
                            lineHeight: 1.2,
                            cursor: waitingForQuestion ? 'default' : 'pointer',
                            opacity: waitingForQuestion ? 0.35 : 1,
                            transition: 'opacity 320ms ease',
                          }}
                        >
                          {choice}
                        </button>
                      ))}
                    </div>
                  )}
                  {gameActions.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px 18px' }}>
                      {gameActions.map((action) => (
                        <button
                          key={`${action.label}:${action.reply}`}
                          type="button"
                          onClick={() => onGameReply?.(action.reply)}
                          disabled={waitingForQuestion}
                          className="lp-btn-link"
                          style={{ color: 'rgba(255,255,255,0.88)' }}
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, letterSpacing: '0.06em' }}>
                {isThinking
                  ? 'Sending your answer…'
                  : isSpeaking
                    ? ' '
                    : wantsPhoto && onOpenAttachments
                      ? 'Attach a photo, or tap to record'
                      : 'Frame yourself, then tap to record'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 28 }}>
                {/* Attach sits left of the record button and Skip right of it;
                    a missing side gets an equal spacer so record stays
                    centred. */}
                {onOpenAttachments ? (
                  <button
                    type="button"
                    onClick={onOpenAttachments}
                    aria-label={wantsPhoto ? 'Attach a photo as your answer' : 'Attach photos, videos or docs'}
                    style={{
                      position: 'relative',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      border: wantsPhoto ? '2px solid var(--lp-pink)' : '1px solid rgba(255,255,255,0.35)',
                      boxShadow: wantsPhoto ? '0 0 18px rgba(236,72,153,0.55)' : undefined,
                      background: 'rgba(0,0,0,0.35)',
                      color: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                    </svg>
                    {attachmentCount > 0 && (
                      <span
                        aria-hidden
                        style={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          minWidth: 18,
                          height: 18,
                          padding: '0 4px',
                          borderRadius: 999,
                          background: 'linear-gradient(135deg, var(--lp-pink), var(--lp-orange))',
                          color: '#fff',
                          fontSize: 10,
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {attachmentCount}
                      </span>
                    )}
                  </button>
                ) : (
                  <span style={{ width: 48 }} aria-hidden />
                )}
                {/* Recording is held while the interviewer is preparing or
                    speaking the next question — otherwise the mic would pick
                    up the question audio at the top of the take. */}
                <button
                  type="button"
                  onClick={handleRecord}
                  disabled={waitingForQuestion}
                  aria-label="Start recording"
                  style={{
                    width: 74,
                    height: 74,
                    borderRadius: '50%',
                    border: '4px solid rgba(255,255,255,0.9)',
                    background: 'transparent',
                    display: 'grid',
                    placeItems: 'center',
                    cursor: waitingForQuestion ? 'default' : 'pointer',
                    opacity: waitingForQuestion ? 0.35 : 1,
                    transition: 'opacity 320ms ease',
                  }}
                >
                  <span
                    aria-hidden
                    style={{ width: 56, height: 56, borderRadius: '50%', background: '#ff3b30' }}
                  />
                </button>
                {onSkip ? (
                  <button
                    type="button"
                    onClick={onSkip}
                    disabled={waitingForQuestion}
                    aria-label="Skip this question"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      border: '1px solid rgba(255,255,255,0.35)',
                      background: 'rgba(0,0,0,0.35)',
                      color: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      cursor: waitingForQuestion ? 'default' : 'pointer',
                      opacity: waitingForQuestion ? 0.35 : 1,
                      transition: 'opacity 320ms ease',
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M5 4v16l11-8z" />
                      <path d="M19 5v14" />
                    </svg>
                  </button>
                ) : (
                  <span style={{ width: 48 }} aria-hidden />
                )}
              </div>
            </>
          )}

          {isRecording && (
            <>
              <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, letterSpacing: '0.06em' }}>
                Tap stop to send your answer
              </div>
              <button
                type="button"
                onClick={() => void stopAndSend()}
                aria-label="Stop recording and send"
                style={{
                  width: 74,
                  height: 74,
                  borderRadius: '50%',
                  border: '4px solid rgba(255,255,255,0.9)',
                  background: 'transparent',
                  display: 'grid',
                  placeItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <span
                  aria-hidden
                  style={{ width: 30, height: 30, borderRadius: 6, background: '#ff3b30' }}
                />
              </button>
            </>
          )}

          {phase === 'error' && (
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                type="button"
                onClick={handleClose}
                className="lp-btn"
                style={{
                  minWidth: 130,
                  background: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  color: '#fff',
                }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => void openCamera(facing)}
                className="lp-btn lp-btn-primary"
                style={{ minWidth: 150 }}
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>

      <style>
        {`@keyframes lp-record-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.35; }
          }`}
      </style>
    </div>
  );
}

export default VideoReplyModal;
